import { hmbpDocumentTypes } from "encamp-shared/src/hmbp";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  CredentialKind,
  CredentialVerificationStatus,
  DocumentType,
  TierIiReportEncampStatus,
  TierIiReportKind,
  TierIiReportOrgStatus,
} from "generated-graphql/graphql";

/**
 * A hybrid of orgStatus and encampStatus. This can drive behavior
 * Where we basically just treat `Verified` as:
 *  (organizationStatus: VERIFIED and encampStatus:!FILING_COMPLETE)
 */
export const CombinedStatusValues = [
  TierIiReportOrgStatus.Assigned,
  TierIiReportOrgStatus.InProgress,
  TierIiReportOrgStatus.InReview,
  TierIiReportOrgStatus.NotReporting,
  TierIiReportOrgStatus.NotStarted,
  TierIiReportOrgStatus.Verified,
  TierIiReportEncampStatus.FilingComplete,
];

export function statusToLabel(
  orgStatus: TierIiReportOrgStatus,
  encampStatus: TierIiReportEncampStatus
): string {
  switch (orgStatus) {
    case TierIiReportOrgStatus.NotStarted:
      return `🔶 ${orgStatusToLabel(orgStatus)}`;
    case TierIiReportOrgStatus.NotReporting:
      return `◻️️ ${orgStatusToLabel(orgStatus)}`;
    case TierIiReportOrgStatus.Assigned:
    case TierIiReportOrgStatus.InProgress:
      return `🚧 ${orgStatusToLabel(orgStatus)}`;
    case TierIiReportOrgStatus.InReview:
      return `🔍 ${orgStatusToLabel(orgStatus)}`;
    case TierIiReportOrgStatus.Verified: // once we get to verified we're in encamp status territory
      if (encampStatus !== TierIiReportEncampStatus.FilingComplete) {
        return "✅ Verified";
      }
      return `🎉 ${encampStatusToLabel(encampStatus)}`;
  }
}

export function orgStatusToLabel(orgStatus: TierIiReportOrgStatus): string {
  switch (orgStatus) {
    case TierIiReportOrgStatus.NotStarted:
      return "Not Started";
    case TierIiReportOrgStatus.NotReporting:
      return "Not Reporting";
    case TierIiReportOrgStatus.Assigned:
      return "Assigned";
    case TierIiReportOrgStatus.InProgress:
      return "In Progress";
    case TierIiReportOrgStatus.InReview:
      return "In Review";
    case TierIiReportOrgStatus.Verified:
      return "Verified";
  }
}

export function orgStatusIsOpen(orgStatus: TierIiReportOrgStatus): boolean {
  switch (orgStatus) {
    case TierIiReportOrgStatus.NotStarted:
    case TierIiReportOrgStatus.Assigned:
    case TierIiReportOrgStatus.InProgress:
    case TierIiReportOrgStatus.InReview:
      return true;
    case TierIiReportOrgStatus.NotReporting:
    case TierIiReportOrgStatus.Verified:
      return false;
  }
}

export const encampStatusToLabel = (encampStatus: TierIiReportEncampStatus): string => {
  switch (encampStatus) {
    case TierIiReportEncampStatus.AwaitingVerification:
      return "Awaiting Verification";
    case TierIiReportEncampStatus.Processing:
      return "Processing";
    case TierIiReportEncampStatus.FilingComplete:
      return "Filing Complete";
    case TierIiReportEncampStatus.NotReporting:
      return "Not Reporting";
  }
};

export const reportKindToLabel = (reportKind: TierIiReportKind): string => {
  switch (reportKind) {
    case TierIiReportKind.Annual:
      return "Annual";

    case TierIiReportKind.Initial:
      return "Initial Notification";

    case TierIiReportKind.NewlyNotReporting:
      return "Newly Not Reporting";

    case TierIiReportKind.Revision:
      return "Revision";

    case TierIiReportKind.Update:
      return "Update";
    default:
      return "Annual";
  }
};

export const CaliforniaDocumentTypes = hmbpDocumentTypes;

/**
 * Curated list of document types shown in the report stepper Add/Edit dialog
 * when addEditDocumentTypeStrategy is "tier-ii" and the facility is NOT HMBP.
 *
 * This is a client-side picker list and should remain a subset of
 * KnownSubmissionDocumentTypes. HMBP-specific types are provided via
 * CaliforniaDocumentTypes when the HMBP feature is active.
 */
export const TierIIDocumentTypes = [
  DocumentType.AstDocumentation,
  DocumentType.DikeDescription,
  DocumentType.EmergencyResponsePlan,
  DocumentType.HazardousMaterialsContingencyPlanOrQuickGuide,
  DocumentType.SiteCoordinateAbbreviation,
  DocumentType.SitePlan,
  DocumentType.TrainingPlan,
  DocumentType.UstDocumentation,
];

export const documentTypeToLabel = (documentType: DocumentType): string => {
  switch (documentType) {
    case DocumentType.AstDocumentation:
      return "AST Documentation";
    case DocumentType.Certification:
      return "Certification";
    case DocumentType.Check:
      return "Check";
    case DocumentType.CoverLetter:
      return "Cover Letter";
    case DocumentType.DikeDescription:
      return "Dike Description";
    case DocumentType.EmergencyResponsePlan:
      return "Emergency Response Plan";
    case DocumentType.Epcra302:
      return "EPCRA 302";
    case DocumentType.Epcra304:
      return "EPCRA 304";
    case DocumentType.Epcra311:
      return "EPCRA 311";
    case DocumentType.Epcra312:
      return "EPCRA 312";
    case DocumentType.Epcra313:
      return "EPCRA 313";
    case DocumentType.FeeCalculation:
      return "Fee Calculation";
    case DocumentType.Fees:
      return "Fees";
    case DocumentType.Form:
      return "Form";
    case DocumentType.HazardousMaterialsContingencyPlanOrQuickGuide:
      return "Hazardous Materials Contingency Plan or Quick Guide";
    case DocumentType.HazardousMaterialsInventoryStatement:
      return "Hazardous Materials Inventory Statement";
    case DocumentType.HazardousMaterialsManagementPlan:
      return "Hazardous Materials Management Plan";
    case DocumentType.HazardousWasteAnnualReport:
      return "Hazardous Waste Annual Report";
    case DocumentType.HazardousWasteBiennialReport:
      return "Hazardous Waste Biennial Report";
    case DocumentType.HazardousWasteContingencyPlan:
      return "Hazardous Waste Contingency Plan";
    case DocumentType.HazardousWasteFeesReceipts:
      return "Hazardous Waste Fees/Receipts";
    case DocumentType.HazardousWasteNotification:
      return "Hazardous Waste Notification";
    case DocumentType.HazardousWasteQuarterlyReport:
      return "Hazardous Waste Quarterly Report";
    case DocumentType.HazardousWasteRecertification:
      return "Hazardous Waste Recertification";
    case DocumentType.Invoice:
      return "Invoice";
    case DocumentType.NegativeDeclaration:
      return "Negative Declaration";
    case DocumentType.Other:
      return "Other";
    case DocumentType.OtherRcra:
      return "Other RCRA report";
    case DocumentType.P2Plan:
      return "P2 Plan";
    case DocumentType.P2Update:
      return "P2 Update";
    case DocumentType.Permit:
      return "Permit";
    case DocumentType.Proof:
      return "Proof";
    case DocumentType.RcraAnnual:
      return "RCRA Annual";
    case DocumentType.RcraFeePaymentOrInvoice:
      return "RCRA Fee Payment/Invoice";
    case DocumentType.RcraManifest:
      return "RCRA Manifest";
    case DocumentType.RcraUpdateNotification:
      return "RCRA Update Notification";
    case DocumentType.Receipt:
      return "Receipt";
    case DocumentType.ReferenceImage:
      return "Reference Image";
    case DocumentType.Report:
      return "Report";
    case DocumentType.RevisionOrUpdate:
      return "Revision/Update";
    case DocumentType.SafeguardMeasures:
      return "Safeguard Measures";
    case DocumentType.SafetyDataSheet:
      return "Safety Data Sheet";
    case DocumentType.Screenshot:
      return "Screenshot";
    case DocumentType.SiteCoordinateAbbreviation:
      return "Site Coordinate Abbreviation";
    case DocumentType.SitePlan:
      return "Site Plan";
    case DocumentType.Submission:
      return "Submission";
    case DocumentType.TrainingPlan:
      return "Training Plan";
    case DocumentType.UstDocumentation:
      return "UST Documentation";
    case DocumentType.UstMonitoringSitePlan:
      return "UST Monitoring Site Plan";
    case DocumentType.UstCertificationOfFinancialResponsibility:
      return "UST Certification of Financial Responsibility";
    case DocumentType.UstResponsePlan:
      return "UST Response Plan";
    case DocumentType.UstOwnerOperatorWrittenAgreement:
      return "UST Owner/Operator: Written Agreement";
    case DocumentType.UstLetterFromChiefFinancialOfficer:
      return "UST Letter from Chief Financial Officer";
    case DocumentType.OwnerStatementOfDesignatedUstOperatorCompliance:
      return "Owner Statement of Designated UST Operator Compliance";
    case DocumentType.RecyclableMaterialsReportDocumentation:
      return "Recyclable Materials Report Documentation";
    case DocumentType.HazardousWasteTankClosureCertificate:
      return "Hazardous Waste Tank Closure Certificate";
    case DocumentType.AboveGroundPetroleumStorageActDocumentation:
      return "Above Ground Petroleum Storage Act Documentation";
    case DocumentType.AboveGroundSeparationContainmentAndMonitoringPlan:
      return "Above Ground Separation, Containment, and Monitoring Plan";
    case DocumentType.SpillPreventionControlAndCountermeasurePlan:
      return "Spill Prevention, Control, and Countermeasure (SPCC) Plan";
    default:
      return "Other";
  }
};

export const credentialKindToLabel = (kind: CredentialKind): string => {
  switch (kind) {
    case CredentialKind.RcraCounty:
      return "RCRA County";
    case CredentialKind.RcraState:
      return "RCRA State";
    case CredentialKind.RcraFederal:
      return "RCRA Federal";
    case CredentialKind.RcraPayment:
      return "RCRA Payment";
    case CredentialKind.Tierii:
      return "Tier II";
    case CredentialKind.Lepc:
      return "LEPC";
  }
};

export const credentialVerificationStatusToLabel = (
  status: CredentialVerificationStatus
): string => {
  switch (status) {
    case CredentialVerificationStatus.InvalidAccessidOrPassword:
      return "Invalid Access ID or Password";
    case CredentialVerificationStatus.UnknownNonVerifiable:
      return "Unknown Non-Verifiable";
    case CredentialVerificationStatus.FailedOneTimePassword:
      return "Failed One-Time Password";
    default:
      return prettyPrintEnumValue(status);
  }
};
